import { Plus, X } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Input, Select } from "../../components/ui/Input";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { useToast } from "../../components/ui/Toast";
import type { Campaign, Experiment } from "../../lib/types";
import { useCampaigns } from "../campaigns/useCampaigns";
import {
	useSaveExperiment,
	type ExperimentDraft,
	type ExperimentGroupDraft,
} from "./useExperiments";

const ID_HELP =
	"In Intelligems, open the test and choose Show Info. Copy the full 36 character IDs, not the short ones labelled for Google Analytics.";

const MIN_GROUPS = 2;

const OUTSIDE_HELP =
	"A shopper is outside the test when we have no group for them: Intelligems didn't respond (blocked by an ad blocker, failed to load, or took over 3 seconds), they aren't in the test's targeting or traffic allocation, or they started checkout before their group reached the cart. They see the campaigns of the group picked here, and get its discounts at checkout. Intelligems leaves them out of its results either way.";

const blankDraft = (): ExperimentDraft => ({
	title: "",
	externalId: "",
	groups: [
		{ name: "Control", externalId: "", isFallback: false },
		{ name: "Variant", externalId: "", isFallback: false },
	],
});

const toDraft = (experiment: Experiment): ExperimentDraft => ({
	title: experiment.title,
	externalId: experiment.externalId,
	groups: experiment.groups.map(({ id, name, externalId, isFallback }) => ({
		id,
		name,
		externalId,
		isFallback,
	})),
});

const groupLabel = (group: ExperimentGroupDraft, index: number): string =>
	group.name.trim() || `Group ${index + 1}`;

const fallbackOptionLabel = (
	group: ExperimentGroupDraft,
	index: number,
	campaigns: readonly Campaign[],
): string => {
	const titles = campaigns
		.filter((campaign) => group.id && campaign.experimentGroupId === group.id)
		.map((campaign) => campaign.title);
	const name = groupLabel(group, index);
	return titles.length > 0 ? `${name}: ${titles.join(", ")}` : `${name} (no campaigns yet)`;
};

/**
 * Mirrors the server's rule: a group with a published campaign carries its
 * fallback flag in that campaign's discount, so the choice cannot move onto
 * or off it.
 */
const publishedGroupIds = (campaigns: readonly Campaign[]): Set<string> =>
	new Set(
		campaigns.flatMap((campaign) =>
			campaign.publishedAt && campaign.experimentGroupId ? [campaign.experimentGroupId] : [],
		),
	);

type ExperimentDialogProps = {
	experiment: Experiment | null;
	onClose: () => void;
};

/** Mounted only while open, so each opening starts from the saved test. */
export const ExperimentDialog = ({ experiment, onClose }: ExperimentDialogProps) => {
	const [draft, setDraft] = useState<ExperimentDraft>(() =>
		experiment ? toDraft(experiment) : blankDraft(),
	);
	const save = useSaveExperiment(experiment?.id ?? null);
	const campaigns = useCampaigns().data?.items ?? [];
	const toast = useToast();

	const published = publishedGroupIds(campaigns);
	const savedFallbackId = experiment?.groups.find((group) => group.isFallback)?.id ?? null;
	const fallbackIndex = draft.groups.findIndex((group) => group.isFallback);
	const canFallBackTo = (groupId: string | null): boolean =>
		groupId === savedFallbackId ||
		[savedFallbackId, groupId].every((id) => !id || !published.has(id));
	const fallbackLocked = draft.groups.some((group) => !canFallBackTo(group.id ?? null));

	const chooseFallback = (index: number) =>
		setDraft({
			...draft,
			groups: draft.groups.map((group, at) => ({ ...group, isFallback: at === index })),
		});

	const setGroup = (index: number, patch: Partial<ExperimentGroupDraft>) =>
		setDraft({
			...draft,
			groups: draft.groups.map((group, at) => (at === index ? { ...group, ...patch } : group)),
		});

	const submit = () =>
		save.mutate(draft, {
			onSuccess: () => {
				toast.push(experiment ? "Test saved" : "Test added");
				onClose();
			},
			onError: (error) => toast.push(error.message, "error"),
		});

	return (
		<Dialog
			open
			onOpenChange={(open) => !open && onClose()}
			title={experiment ? "Edit A/B test" : "Connect an Intelligems test"}
			description="Create a Content test in Intelligems with no edits, then map its groups here. Intelligems splits the traffic and reports the results; AOVtools shows each group its campaigns."
			footer={
				<>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={save.isPending || !draft.title.trim()}>
						{experiment ? "Save" : "Add test"}
					</Button>
				</>
			}
		>
			<div className="space-y-4">
				<label className="block space-y-1.5">
					<span className="text-sm text-ink">Name</span>
					<Input
						value={draft.title}
						onChange={(event) => setDraft({ ...draft, title: event.target.value })}
						placeholder="Free gift at $50 vs $75"
					/>
				</label>

				<label className="block space-y-1.5">
					<LabelWithHint label="Intelligems test ID">{ID_HELP}</LabelWithHint>
					<Input
						value={draft.externalId}
						onChange={(event) => setDraft({ ...draft, externalId: event.target.value })}
						className="font-mono text-xs"
					/>
				</label>

				<div className="space-y-2">
					<span className="block text-sm text-ink">Groups</span>
					{draft.groups.map((group, index) => (
						<div key={group.id ?? `new-${index}`} className="flex items-center gap-2">
							<Input
								value={group.name}
								onChange={(event) => setGroup(index, { name: event.target.value })}
								aria-label={`Group ${index + 1} name`}
								className="w-32"
							/>
							<Input
								value={group.externalId}
								onChange={(event) => setGroup(index, { externalId: event.target.value })}
								aria-label={`Group ${index + 1} Intelligems ID`}
								placeholder="Intelligems group ID"
								className="min-w-0 flex-1 font-mono text-xs"
							/>
							{draft.groups.length > MIN_GROUPS ? (
								<Button
									variant="ghost"
									size="sm"
									aria-label={`Remove ${group.name || `group ${index + 1}`}`}
									onClick={() =>
										setDraft({ ...draft, groups: draft.groups.filter((_, at) => at !== index) })
									}
								>
									<X className="h-4 w-4" strokeWidth={1.75} />
								</Button>
							) : null}
						</div>
					))}
					<Button
						variant="ghost"
						size="sm"
						onClick={() =>
							setDraft({
								...draft,
								groups: [...draft.groups, { name: "", externalId: "", isFallback: false }],
							})
						}
					>
						<Plus className="h-4 w-4" strokeWidth={2} />
						Add group
					</Button>
				</div>

				<label className="block space-y-1.5">
					<LabelWithHint label="Shoppers outside the test see">{OUTSIDE_HELP}</LabelWithHint>
					<Select
						value={fallbackIndex === -1 ? "" : String(fallbackIndex)}
						onChange={(event) =>
							chooseFallback(event.target.value === "" ? -1 : Number(event.target.value))
						}
					>
						<option value="" disabled={!canFallBackTo(null)}>
							None of this test's campaigns
						</option>
						{draft.groups.map((group, index) => (
							<option
								key={group.id ?? `new-${index}`}
								value={index}
								disabled={!canFallBackTo(group.id ?? null)}
							>
								{fallbackOptionLabel(group, index, campaigns)}
							</option>
						))}
					</Select>
					{fallbackLocked ? (
						<p className="text-xs text-ink-muted">
							Groups with a published campaign are fixed, since its discount already carries this
							choice.
						</p>
					) : null}
				</label>
			</div>
		</Dialog>
	);
};
